import { RefObject, useEffect, useRef, useState } from 'react';
import { useBreakpoints } from '@fiverr-private/hooks';
import { theme } from '@fiverr-private/theme';

const getBannerHeight = (): number => {
    const banner = document.getElementById('AppBanner');
    if (!banner) {
        return 0;
    }
    const { position } = getComputedStyle(banner);
    return position === 'sticky' || position === 'fixed' ? banner.offsetHeight : 0;
};

type Breakpoint = keyof typeof theme.numericBreakpoints;

interface UseStickyTriggersOptions<T extends Record<string, string>> {
    headerRef: RefObject<HTMLElement>;
    /**
     * Map of result-key → CSS selector. Each selector is observed via a single
     * IntersectionObserver; when an element exits the header area the
     * corresponding key flips to `true`.
     */
    selectors: T;
    /**
     * When provided, the observers only activate when the viewport is strictly
     * BELOW this Penta breakpoint (e.g. 'lg' → active when width < 1160px).
     * Uses useBreakpoints() from @fiverr-private/hooks — aligned with Penta design system.
     */
    belowBreakpoint?: Breakpoint;
}

type UseStickyTriggersResult<T extends Record<string, string>> = { [K in keyof T]: boolean };

export const useStickyTriggers = <T extends Record<string, string>>({
    headerRef,
    selectors,
    belowBreakpoint,
}: UseStickyTriggersOptions<T>): UseStickyTriggersResult<T> => {
    const initialState = Object.fromEntries(
        Object.keys(selectors).map((k) => [k, false])
    ) as UseStickyTriggersResult<T>;

    const [results, setResults] = useState<UseStickyTriggersResult<T>>(initialState);

    // Stable refs so the effect closure doesn't go stale
    const selectorsRef = useRef(selectors);
    const breakpoints = useBreakpoints();

    // When belowBreakpoint is set, only activate when viewport is below that breakpoint
    const isActive = belowBreakpoint ? !breakpoints[belowBreakpoint] : true;

    useEffect(() => {
        let observer: IntersectionObserver | null = null;

        const setup = () => {
            const header = headerRef.current;
            if (!header) {
                return;
            }

            const headerHeight = header.offsetHeight;
            const bannerHeight = getBannerHeight();

            // One observer, multiple observed elements — entry.target maps back to key
            const elementKeyMap = new Map<Element, keyof T>();

            observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        const key = elementKeyMap.get(entry.target as Element);
                        if (key !== undefined) {
                            setResults((prev) => ({ ...prev, [key]: !entry.isIntersecting }));
                        }
                    });
                },
                { threshold: 0, rootMargin: `-${headerHeight + bannerHeight}px 0px 0px 0px` }
            );

            Object.entries(selectorsRef.current).forEach(([key, selector]) => {
                if (!selector) {
                    return;
                }
                const element = document.querySelector(selector);
                if (element) {
                    elementKeyMap.set(element, key as keyof T);
                    observer!.observe(element);
                }
            });
        };

        const teardown = () => {
            observer?.disconnect();
            observer = null;
            setResults(
                Object.fromEntries(
                    Object.keys(selectorsRef.current).map((k) => [k, false])
                ) as UseStickyTriggersResult<T>
            );
        };

        if (isActive) {
            setup();
        }

        return teardown;
    }, [headerRef, isActive]);

    return results;
};
